/**
 * TanStack Query Hooks for Content Access Control
 *
 * Provides React hooks for managing gated content access with
 * automatic caching, loading states, and secure API calls
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  submitAccessRequest,
  checkAccess,
  listAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
  revokeAccess,
  type AccessRequestInput,
} from '@/lib/api/access-control'

// Re-export types for convenience (used by consuming components)
export type {
  AccessRequestInput,
  ContentAccessRequest,
  AccessCheckResult,
} from '@/lib/api/access-control'

// ============================================================================
// Public Hooks - Access Request
// ============================================================================

/**
 * Submit a request for access to gated content
 * No authentication required
 */
export function useSubmitAccessRequest() {
  return useMutation({
    mutationFn: (input: AccessRequestInput) => submitAccessRequest(input, ''), // Pass empty secureCvId
  })
}

/**
 * Check if user has valid access token
 * No authentication required, but token-protected
 */
export function useCheckAccess(token?: string, contentType?: string) {
  return useQuery({
    queryKey: ['access-check', token, contentType],
    queryFn: () => checkAccess(token!, contentType!),
    enabled: !!token && !!contentType,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Don't retry on failure (likely invalid token)
    retry: false,
  })
}

// ============================================================================
// Admin Hooks - Access Management
// ============================================================================

/**
 * Get list of access requests (admin only)
 * Requires authentication
 */
export function useAccessRequests(status?: 'pending' | 'approved' | 'denied') {
  return useQuery({
    queryKey: ['access-requests', status],
    queryFn: () => listAccessRequests(status),
    // Refetch every 30 seconds to stay updated
    refetchInterval: 30000,
  })
}

/**
 * Approve an access request (admin only)
 * Requires authentication
 */
export function useApproveAccessRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      requestId,
      grantedContent,
      expiresInDays,
    }: {
      requestId: string
      grantedContent: string[]
      expiresInDays?: number
    }) => approveAccessRequest(requestId, grantedContent, expiresInDays),
    onSuccess: () => {
      // Invalidate access requests list to refresh
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}

/**
 * Deny an access request (admin only)
 * Requires authentication
 */
export function useDenyAccessRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      denyAccessRequest(requestId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}

/**
 * Revoke previously granted access (admin only)
 * Requires authentication
 */
export function useRevokeAccess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ grantId, reason }: { grantId: string; reason?: string }) =>
      revokeAccess(grantId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}
